import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      websiteStyle: {
        bigBold: "font-bold rounded-2xl px-5 py-3.5",
        modernClean: "font-medium rounded-lg px-5 py-4",
        traditional: "font-serif font-medium px-5 py-4 tracking-wide",
        posterBold: "font-bold rounded-md px-8 py-4 text-base",
      },
      variant: {
        default: "border-2 border-transparent bg-primary text-primary-foreground transition hover:bg-primary/90",
        destructive:
          "border-2 border-transparent bg-destructive text-destructive-foreground transition hover:bg-destructive/90",
        outline:
          "border-2 border-primary text-foreground bg-background transition hover:bg-primary hover:text-primary-foreground",
        secondary: "border-2 border-transparent bg-secondary text-secondary-foreground transition hover:bg-secondary/80",
        ghost: "transition border-2 border-input hover:bg-primary hover:text-primary-foreground",
      },
      size: {
        default: "",
        sm: "text-sm",
        lg: "text-lg",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      websiteStyle: "modernClean",
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, websiteStyle, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn("group/button", buttonVariants({ variant, size, websiteStyle, className }))}
        ref={ref}
        data-feedback-eligible
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

// MovingArrow component for animated arrow
interface MovingArrowProps {
  className?: string;
}

const MovingArrow: React.FC<MovingArrowProps> = ({ className }) => (
  <span
    className={cn(
      "ml-1 inline-block transition-transform duration-300 group-hover/button:translate-x-1",
      className
    )}
  >
    {"→"}
  </span>
);

const StaticArrow: React.FC<{
  className?: string;
}> = ({ className }) => <span className={cn("ml-1 inline-block", className)}>{"→"}</span>;

export { Button, buttonVariants, MovingArrow, StaticArrow };
